/**
 * Tooltips. A tip opens 150 ms after the pointer rests or a keyboard lands on
 * its target, closes on leave, blur, a press or Escape, and never takes focus.
 * `Tip` wraps any content; `useTip` lets a control carry its own tip without
 * a wrapper (an icon-only Button does). Inside a Tip, a Button adds none.
 */
import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

const DELAY = 150;
export const InTip = createContext(false);
export const useInTip = () => useContext(InTip);

type Bind = {
  onMouseEnter: (e: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave: () => void;
  onFocus: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur: () => void;
  onMouseDown: () => void;
};

export function useTip(text: ReactNode, side: "top" | "right" = "top"): { bind: Bind; node: ReactNode } {
  const [at, setAt] = useState<DOMRect | null>(null);
  const tip = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [style, setStyle] = useState<React.CSSProperties>({ visibility: "hidden" });
  useLayoutEffect(() => {
    if (!at || !tip.current) return;
    const w = tip.current.offsetWidth;
    const h = tip.current.offsetHeight;
    if (side === "right" && at.right + 10 + w < window.innerWidth) {
      setStyle({ left: at.right + 10, top: Math.max(8, Math.min(window.innerHeight - h - 8, at.top + at.height / 2 - h / 2)) });
      return;
    }
    const left = Math.max(8, Math.min(window.innerWidth - w - 8, at.left + at.width / 2 - w / 2));
    const top = at.top - h - 8;
    setStyle({ left, top: top < 8 ? at.bottom + 8 : top });
  }, [at, side]);
  const hide = () => {
    clearTimeout(timer.current);
    setAt(null);
    setStyle({ visibility: "hidden" });
  };
  // Escape dismisses the tip without moving focus; the key still reaches the layer below.
  useEffect(() => {
    if (!at) return;
    const fn = (e: KeyboardEvent) => e.key === "Escape" && hide();
    window.addEventListener("keydown", fn, true);
    return () => window.removeEventListener("keydown", fn, true);
  }, [at]);
  useEffect(() => () => clearTimeout(timer.current), []);
  const later = (el: HTMLElement) => {
    if (!text) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => el.isConnected && setAt(el.getBoundingClientRect()), DELAY);
  };
  const bind: Bind = {
    onMouseEnter: (e) => later(e.currentTarget),
    onMouseLeave: hide,
    // A keyboard landing shows the tip; focus a script restores after a click does not.
    onFocus: (e) => {
      const t = e.target as HTMLElement;
      if (typeof t.matches !== "function" || t.matches(":focus-visible")) later(e.currentTarget);
    },
    onBlur: hide,
    onMouseDown: hide,
  };
  const node =
    at && text
      ? createPortal(
          <div className="tip" ref={tip} style={style} role="tooltip">
            {text}
          </div>,
          document.body,
        )
      : null;
  return { bind, node };
}

export function Tip({ text, children, side = "top", block }: { text: ReactNode; children: ReactNode; side?: "top" | "right"; block?: boolean }) {
  const { bind, node } = useTip(text, side);
  return (
    <span style={block ? { display: "block", minWidth: 0 } : { display: "inline-flex", minWidth: 0 }} {...bind}>
      <InTip.Provider value={true}>{children}</InTip.Provider>
      {node}
    </span>
  );
}
